/* eslint-disable */
import React, { useMemo } from 'react';
import { Viewer, Worker, SpecialZoomLevel } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { Modal, Box, IconButton, Paper, Typography } from '@mui/material';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { Download, X, FileText } from 'lucide-react';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90vw',
    maxWidth: '1000px',
    height: '90vh',
    maxHeight: '700px',
    bgcolor: 'background.paper',
    borderRadius: 2,
    boxShadow: 24,
    outline: 'none',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 99999,
};

export const getPrDocType = (docUrl) => {
    if (!docUrl) return 'none';
    if (docUrl.startsWith('data:application/pdf') || /\.pdf($|\?)/i.test(docUrl)) return 'pdf';
    if (docUrl.startsWith('data:image/') || /\.(png|jpe?g|gif|webp)($|\?)/i.test(docUrl)) return 'image';
    return 'other';
};

export const downloadPrDoc = (docUrl, fileName) => {
    if (!docUrl) return;
    const link = document.createElement('a');
    link.href = docUrl;
    link.download = fileName || 'document';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

// Slim toolbar: page nav + zoom only, small footprint.
const renderCompactToolbar = (Toolbar) => (
    <Toolbar>
        {(slots) => {
            const {
                CurrentPageInput, NumberOfPages,
                GoToNextPage, GoToPreviousPage,
                ZoomIn, ZoomOut, Zoom,
            } = slots;
            return (
                <div className='flex items-center justify-center w-full gap-0.5 text-xs'>
                    <GoToPreviousPage />
                    <div className='flex items-center gap-1 text-gray-600'>
                        <CurrentPageInput /> / <NumberOfPages />
                    </div>
                    <GoToNextPage />
                    <div className='w-3' />
                    <ZoomOut />
                    <Zoom />
                    <ZoomIn />
                </div>
            );
        }}
    </Toolbar>
);

// Inline preview (no modal) — renders the doc itself; parent controls the frame.
export function PRDocPreview({ docUrl, fileName }) {
    const defaultLayoutPluginInstance = defaultLayoutPlugin({
        renderToolbar: renderCompactToolbar,
        sidebarTabs: () => [],
    });
    const docType = useMemo(() => getPrDocType(docUrl), [docUrl]);

    if (docType === 'pdf') {
        return (
            <div className='h-full w-full'>
                <Worker workerUrl='https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js'>
                    <Viewer
                        fileUrl={docUrl}
                        defaultScale={SpecialZoomLevel.PageWidth}
                        plugins={[defaultLayoutPluginInstance]}
                    />
                </Worker>
            </div>
        );
    }

    if (docType === 'image') {
        return (
            <div className='h-full w-full flex items-center justify-center overflow-auto bg-gray-50'>
                <img
                    src={docUrl}
                    alt={fileName || 'Attached Document'}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
            </div>
        );
    }

    return (
        <div className='h-full w-full flex flex-col items-center justify-center gap-3 text-gray-500'>
            <FileText size={40} />
            <span className='text-sm'>
                {docType === 'none'
                    ? 'No document attached.'
                    : 'Preview not available for this file type.'}
            </span>
            {docType === 'other' && (
                <button
                    type='button'
                    onClick={() => downloadPrDoc(docUrl, fileName)}
                    className='flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md transition-colors'
                >
                    <Download size={16} />
                    Download
                </button>
            )}
        </div>
    );
}

function PRDocViewer({ open, onClose, docUrl, fileName }) {
    return (
        <Modal open={open} onClose={onClose} sx={{ zIndex: 99999 }}>
            <Box sx={modalStyle}>
                {/* Header */}
                <Box sx={{
                    px: 3,
                    py: 2,
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <FileText size={18} className='text-blue-600 shrink-0' />
                        <Typography variant='subtitle1' sx={{ color: '#111827', fontWeight: 600 }} noWrap>
                            {fileName || 'Attached Document'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton
                            onClick={() => downloadPrDoc(docUrl, fileName)}
                            title='Download'
                            sx={{ color: '#2563eb', '&:hover': { color: '#1d4ed8' } }}
                        >
                            <Download size={20} />
                        </IconButton>
                        <IconButton
                            onClick={onClose}
                            sx={{ color: '#6b7280', '&:hover': { color: '#374151' } }}
                        >
                            <X size={22} />
                        </IconButton>
                    </Box>
                </Box>

                {/* Doc Preview */}
                <Box sx={{ flex: 1, overflow: 'hidden', p: 1 }}>
                    <Paper
                        elevation={1}
                        sx={{
                            height: '100%',
                            border: '1px solid #e5e7eb',
                            overflow: 'hidden',
                        }}
                    >
                        <PRDocPreview docUrl={docUrl} fileName={fileName} />
                    </Paper>
                </Box>
            </Box>
        </Modal>
    );
}

export default PRDocViewer;
